import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useEffect, useMemo } from 'react';
import { createEventBus } from '../../../../emitter';
import type { BlockRendererProps } from '../../../context/RegistryProvider/types';
import { useInterfaceStore } from '../../../stores/InterfaceStore';
import type { InterfaceEventBusType } from '../../../types/eventBus';
import { usePageInstanceId } from '../../../context/PageInstanceIdProvider';

/**
 * Mode-agnostic responder — wired into BOTH the builder leaf
 * (`BuilderBlockRendererBody`) and the runner leaf (`BlockRenderer` in
 * `runner.tsx`). Crucial that this NOT live in builder-only effects: a
 * TemplateComponent's inner page runs in PREVIEW mode (per
 * `useTemplateComponentBlock`'s `innerMode = pageMode === BUILDER ? PREVIEW :
 * pageMode` flip), so TC source blocks wouldn't respond if we only set up the
 * responder on the builder path.
 *
 * On `requestAllBlocksComputedStates` (broadcast from the per-instance loader
 * generator), the responder publishes this block's `computedBlockState` —
 * the SAME object `withBlockComputations` produced and the renderer is
 * painting against — under `provideBlockComputedState`. The requester
 * collects responses keyed by `blockId` and feeds them into
 * `generateLoaderBlocks` so cloned source blocks' dynamic expressions are
 * frozen against the renderer's authoritative resolved values (no
 * re-evaluation).
 *
 * Why broadcast (no `blockId` on the request) over targeted (one publish per
 * needed id): the requester would otherwise have to walk both the template
 * and consumer-slot subtrees up-front to enumerate ids, AND pay one publish
 * per id. Broadcast is one publish; each block's responder filter is cheap
 * (object equality on stable strings). The trade-off is that blocks the
 * loader doesn't actually need also reply — the requester ignores those.
 */
export function useBlockComputedStateResponder({
  block,
  computedBlockState,
}: {
  block: BlockRendererProps['block'];
  computedBlockState: BlockRendererProps['computedBlockState'];
}) {
  const instanceId = usePageInstanceId();
  const interfaceId = useInterfaceStore().hooks.record.useId();
  const interfaceEventBus = useMemo(
    () => createEventBus<InterfaceEventBusType>(interfaceId || 'default'),
    [interfaceId],
  );

  // `useEventCallback` lets the subscriber always see the latest
  // `computedBlockState` without re-subscribing on every render.
  const handleRequest = useEventCallback(
    (data: InterfaceEventBusType['requestAllBlocksComputedStates']) => {
      if (!data) return;
      interfaceEventBus.publish('provideBlockComputedState', {
        blockId: block.id,
        requestId: data.requestId,
        instanceId,
        computedBlockState,
      });
    },
  );

  useEffect(() => {
    const unsubscribe = interfaceEventBus.subscribe(
      'requestAllBlocksComputedStates',
      handleRequest,
    );
    return () => {
      unsubscribe();
    };
  }, [handleRequest, interfaceEventBus]);
}
